import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Form } from "@/components/ui/form";
import { FormSelectSearchable } from "@/components/forms/form-select-searchable";
import { FormMultipleComboboxAsync } from "@/components/forms";
import { Separator } from "@/components/ui/separator";

import { CreateTechnician } from "@/hooks/services/pps/handlers/technicians-api";
import {
  UsersPaged,
  UserItem,
} from "@/hooks/services/workchestra/handler/user-api";
import { ShopList, ShopItem } from "@/hooks/services/pps/handlers/shop-api";
import { WorkspaceList } from "@/hooks/services/pps/handlers/workspace-api";
import { machineMultipleApiConfig } from "./search-combobox-configs";

import { FormInput } from "@/components/forms";
import {
  useGetData,
  useInvalidateQueries,
} from "@/hooks/services/use-query-hooks";
import type { SelectSearchableOption } from "@/components/forms/form-select-searchable";
import { useWorkSpaceStore } from "@/stores/store";
import { usePostData } from "@/hooks/services/use-query-hooks";
import { toastManager } from "@/lib/toast-manager";
import { SaveOutlined } from "@mui/icons-material";

const formSchema = z.object({
  userId: z.coerce.number().min(1, "Please select a user"),
  name: z.string().min(1, "Name is required"),
  shopId: z.coerce.number().min(1, "Please select a shop"),
  workspaceId: z.coerce.number().min(1, "Workspace ID is required"),
  machineIds: z.array(z.number()).optional().default([]),
});

type FormData = z.infer<typeof formSchema>;

interface AddTechnicianSheetProps {
  onSuccess?: () => void;
  trigger?: React.ReactNode;
}

export function AddTechnicianSheet({
  onSuccess,
  trigger,
}: AddTechnicianSheetProps) {
  const [open, setOpen] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState<any>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number>(0);
  const [selectedShopId, setSelectedShopId] = useState<number>(0);
  const { workspace } = useWorkSpaceStore();
  const invalidate = useInvalidateQueries();

  // Fetch workspaces data
  const { data: workspacesData } = useGetData<{ data: any[] }>(
    WorkspaceList({ page: 1, pageSize: 100 }),
    { enabled: open },
  );

  // Fetch users data
  const { data: usersData } = useGetData<{ items: UserItem[] }>(
    UsersPaged({ currentPage: 1, itemsPerPage: 100, searchText: "" }),
    { enabled: open },
  );

  const workspaceOptions = useMemo(
    () =>
      workspacesData?.data?.map((ws) => ({
        value: ws.id,
        label: ws.workspaceName,
        raw: ws,
      })) || [],
    [workspacesData],
  );

  // Fetch shops data filtered by selected workspace
  const workspaceId = selectedWorkspaceId || workspace.id || 0;
  const { data: shopsData } = useGetData<{ data: ShopItem[] }>(
    ShopList({ search: "", workspaceId }),
    { enabled: open && !!workspaceId && workspaceId > 0 },
  );

  // Transform users to options
  const userOptions: SelectSearchableOption<UserItem>[] =
    usersData?.items?.map((user) => ({
      value: user.id,
      label: `${user.userName} - ${user.fullName}`,
      raw: user,
    })) || [];

  const shopOptions = useMemo(
    () =>
      shopsData?.data?.map((shop) => ({
        value: shop.id,
        label: shop.shopName,
        raw: shop,
      })) || [],
    [shopsData],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      userId: 0,
      name: "",
      shopId: 0,
      workspaceId: workspace.id || 0,
      machineIds: [],
    },
  });

  // Reset form and state when sheet opens
  useEffect(() => {
    if (open) {
      form.reset({
        userId: 0,
        name: "",
        shopId: 0,
        workspaceId: workspace.id || 0,
        machineIds: [],
      });
      setSelectedTechnician(null);
      setSelectedWorkspaceId(workspace.id || 0);
      setSelectedShopId(0);
    }
  }, [open, workspace.id, form]);

  // Watch workspace and shop changes
  const watchedWorkspaceId = form.watch("workspaceId");
  const watchedShopId = form.watch("shopId");

  useEffect(() => {
    if (watchedWorkspaceId !== selectedWorkspaceId) {
      setSelectedWorkspaceId(watchedWorkspaceId);
    }
  }, [watchedWorkspaceId, selectedWorkspaceId]);

  useEffect(() => {
    if (watchedShopId !== selectedShopId) {
      setSelectedShopId(watchedShopId);
    }
  }, [watchedShopId, selectedShopId]);

  // Reset shop and machines when workspace changes
  useEffect(() => {
    setSelectedShopId(0);
    form.setValue("shopId", 0);
    form.setValue("machineIds", []);
  }, [selectedWorkspaceId, form]);

  // Reset machines when shop changes
  useEffect(() => {
    form.setValue("machineIds", []);
  }, [selectedShopId, form]);

  const createTechnicianMutation = usePostData(CreateTechnician());

  const onSubmit = async (data: FormData) => {
    createTechnicianMutation.mutate(
      {
        ...data,
        employeeNumber: selectedTechnician.userName,
      },
      {
        onSuccess: () => {
          toastManager.showSuccess("Technician created successfully");

          form.reset();
          setSelectedTechnician(null);
          setSelectedWorkspaceId(workspace.id || 0);
          setSelectedShopId(0);
          setOpen(false);
          onSuccess?.();
        },
        onError: (error: any) => {
          toastManager.showError(
            "Failed to create technician",
            error?.message || "Something went wrong",
          );
        },
        onSettled: () => {
          invalidate([
            ["TechnicianList"],
            ["TechnicianGroupedByShop"],
            ["MachineListGroupedByShop"],
            ["TechnicianGroupedByShop"],
            ["JobSchedule"],
            ["TechnicianSchedule"],
            ["MachineSchedule"],
          ]);
        },
      },
    );
  };

  const handleCancel = () => {
    form.reset();
    setSelectedTechnician(null);
    setSelectedWorkspaceId(workspace.id || 0);
    setSelectedShopId(0);
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen} modal={false}>
      <SheetTrigger asChild>
        {trigger || (
          <Button
            intent="primary"
            className="aero:h-10 aero:gap-2 aero:rounded aero:px-3 aero:py-2 aero:ml-2"
          >
            <Plus className="aero:h-4 aero:w-4" />
            Add New Technician
          </Button>
        )}
      </SheetTrigger>
      <SheetContent className="aero:w-full aero:sm:max-w-[500px] aero:p-0 aero:flex aero:flex-col aero:gap-0">
        <SheetHeader className="aero:p-6 aero:pb-4">
          <div className="aero:flex aero:items-center aero:justify-between">
            <SheetTitle className="aero:font-inter aero:font-bold aero:text-xl aero:leading-9 aero:text-neutral-900">
              Add New Technician
            </SheetTitle>
          </div>
          <Separator className="aero:mt-4" />
        </SheetHeader>

        <div className="aero:flex-1 aero:overflow-y-auto aero:px-6">
          <Form {...form}>
            <form className="aero:space-y-4">
              <FormSelectSearchable
                control={form.control}
                name="userId"
                label="User"
                placeholder="Select user..."
                searchPlaceholder="Search users..."
                options={userOptions}
                required
                labelClassName="aero:text-sm aero:font-medium aero:text-neutral-700"
                wrapperClassName="aero:space-y-2"
                onValueChange={(value) => {
                  const numericValue =
                    typeof value === "string" ? Number(value) : value;
                  if (numericValue && !Number.isNaN(numericValue)) {
                    // Ensure RHF has the numeric userId as value
                    form.setValue("userId", numericValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });

                    const user = userOptions.find(
                      (opt) => opt.value === numericValue,
                    );
                    if (user?.raw) {
                      setSelectedTechnician(user.raw);
                      form.setValue("name", user.raw.fullName ?? "", {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                      form.clearErrors("name");
                    }
                  } else {
                    setSelectedTechnician(null);
                    form.setValue("userId", 0, { shouldValidate: true });
                    form.setValue("name", "", { shouldValidate: true });
                  }
                }}
                disabled={createTechnicianMutation.isPending}
              />

              <FormInput
                control={form.control}
                name="name"
                label="Name"
                placeholder="Auto-filled"
                disabled
                labelClassName="aero:text-sm aero:font-medium aero:text-neutral-700"
                wrapperClassName="aero:space-y-2"
              />

              <FormSelectSearchable
                control={form.control}
                name="workspaceId"
                label="Workspace"
                options={workspaceOptions}
                placeholder="Select a workspace"
                triggerClassName="aero:h-10"
                required
                labelClassName="aero:text-sm aero:font-medium aero:text-neutral-700"
                wrapperClassName="aero:space-y-2"
                disabled={createTechnicianMutation.isPending}
              />

              <FormSelectSearchable
                control={form.control}
                name="shopId"
                label="Shop"
                placeholder="Select shop..."
                searchPlaceholder="Search shops..."
                options={shopOptions}
                required
                labelClassName="aero:text-sm aero:font-medium aero:text-neutral-700"
                wrapperClassName="aero:space-y-2"
                disabled={createTechnicianMutation.isPending}
              />

              <div className="aero:space-y-3">
                <FormMultipleComboboxAsync
                  control={form.control}
                  name="machineIds"
                  label="Machines"
                  placeholder="Search and select machines..."
                  wrapperClassName="aero:space-y-2"
                  labelClassName="aero:text-sm aero:font-medium aero:text-neutral-700"
                  apiConfig={machineMultipleApiConfig(
                    selectedWorkspaceId || 0,
                    selectedShopId,
                  )}
                  disabled={
                    !selectedShopId || createTechnicianMutation.isPending
                  }
                />
              </div>

              {selectedShopId === 0 && (
                <div className="aero:text-xs aero:text-amber-600 aero:bg-amber-50 aero:p-3 aero:rounded-md aero:border aero:border-amber-200">
                  Please select a shop first to enable machine selection
                </div>
              )}
            </form>
          </Form>
        </div>

        <div className="aero:flex aero:gap-4 aero:p-6 aero:pt-4 aero:border-t aero:border-neutral-200 aero:space-y-3">
          <Button
            onClick={handleCancel}
            disabled={createTechnicianMutation.isPending}
            intent="neutral"
            design="outline"
            className="aero:w-full aero:h-10"
          >
            <X className="aero:w-4 aero:h-4" />
            Cancel
          </Button>
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={createTechnicianMutation.isPending}
            className="aero:w-full aero:h-10"
            intent="primary"
          >
            <SaveOutlined className="aero:w-4 aero:h-4" />
            {createTechnicianMutation.isPending ? (
              <>
                <span className="aero:animate-spin aero:w-4 aero:h-4 aero:border-2 aero:border-white aero:border-t-transparent aero:rounded-full" />
                Adding...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
